import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { BRAND_COLORS as C } from '@/lib/brandColors';

interface AccessDeniedProps {
  title?: string;
  message?: string;
  onUpgrade?: () => void;
  upgradeText?: string;
  /** True when billing/tier says member but the lesson API still returned 403 (sync lag). */
  isMember?: boolean;
  onRetry?: () => void;
}

export const AccessDenied: React.FC<AccessDeniedProps> = ({
  title,
  message,
  onUpgrade,
  upgradeText = 'Upgrade Now',
  isMember = false,
  onRetry,
}) => {
  const resolvedTitle =
    title ?? (isMember ? 'Almost there' : 'Premium Content');
  const resolvedMessage =
    message ??
    (isMember
      ? 'Your membership is activating. This usually takes a few seconds — tap retry to refresh access.'
      : 'Upgrade your membership to access this lesson and unlock all premium features.');

  return (
    <View style={styles.root}>
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          <Ionicons
            name={isMember ? 'time-outline' : 'lock-closed'}
            size={28}
            color={C.accent}
          />
        </View>

        <Text style={styles.title}>{resolvedTitle}</Text>
        <Text style={styles.message}>{resolvedMessage}</Text>

        {isMember && onRetry ? (
          <Pressable
            onPress={onRetry}
            style={({ pressed }) => [styles.primaryBtn, pressed && styles.primaryBtnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Retry access"
          >
            <Text style={styles.primaryBtnText}>Try again</Text>
          </Pressable>
        ) : null}

        {!isMember && onUpgrade ? (
          <Pressable
            onPress={onUpgrade}
            style={({ pressed }) => [styles.primaryBtn, pressed && styles.primaryBtnPressed]}
            accessibilityRole="button"
            accessibilityLabel={upgradeText}
          >
            <Text style={styles.primaryBtnText}>{upgradeText}</Text>
          </Pressable>
        ) : null}

        {isMember && onUpgrade ? (
          <Pressable
            onPress={onUpgrade}
            style={styles.secondaryBtn}
            accessibilityRole="button"
            accessibilityLabel="View membership"
          >
            <Text style={styles.secondaryBtnText}>View membership</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 32,
    backgroundColor: C.pageBg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: C.cardBg,
    borderRadius: 16,
    borderTopWidth: 3,
    borderTopColor: C.accent,
    paddingHorizontal: 24,
    paddingVertical: 28,
    alignItems: 'center',
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#FEF3EF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '700',
    color: C.text,
    textAlign: 'center',
    marginBottom: 10,
  },
  message: {
    fontFamily: 'Inter',
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '400',
    color: C.muted,
    textAlign: 'center',
    marginBottom: 20,
  },
  primaryBtn: {
    width: '100%',
    maxWidth: 280,
    backgroundColor: C.accent,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  primaryBtnPressed: {
    backgroundColor: '#E05A30',
  },
  primaryBtnText: {
    fontFamily: 'Inter',
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryBtn: {
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  secondaryBtnText: {
    fontFamily: 'Inter',
    color: C.muted,
    fontSize: 14,
    fontWeight: '500',
  },
});
